import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatRupees, formatRupeesCompact, formatRupeesVisual, formatPercent } from "@/lib/format";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Link } from "react-router-dom";
import { ArrowUpRight, AlertTriangle, TrendingUp, TrendingDown, Building2, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DateRangeFilter, { computeRange } from "@/components/DateRangeFilter";
import FYPicker, { fyToRange } from "@/components/FYPicker";

const CHART_COLORS = ["#DC2626", "#F59E0B", "#0EA5E9", "#16A34A", "#7C3AED", "#EC4899", "#10B981"];

const INVESTMENT_TYPE_LABEL = {
  land: "Land",
  loan: "Hand Loan",
  company_share: "Company Share",
  business_franchise: "Business",
  sebi_startup_fund: "Startup Fund",
  sip: "SIP",
  lumpsum: "Lumpsum",
};

const KPI = ({ label, value, sub, testid, positive, negative }) => (
  <div
    data-testid={testid}
    className="p-4 sm:p-5 border border-slate-200 bg-white flex flex-col gap-1.5 hover-lift rounded-md"
  >
    <div className="eyebrow">{label}</div>
    <div className="font-mono-fin font-medium text-[22px] sm:text-2xl md:text-[28px] text-slate-900 tracking-tight leading-none mt-1">
      {value}
    </div>
    {sub && (
      <div className={`text-[11px] flex items-center gap-1 mt-0.5 ${
        positive ? "text-green-700" : negative ? "text-red-600" : "text-slate-500"
      }`}>
        {positive && <TrendingUp className="h-3 w-3" />}
        {negative && <TrendingDown className="h-3 w-3" />}
        {sub}
      </div>
    )}
  </div>
);

const ViewToggle = ({ view, setView, hasMy }) => (
  <div className="inline-flex items-center bg-slate-100 rounded-lg p-1" data-testid="view-toggle">
    <button
      onClick={() => setView("company")}
      data-testid="view-company"
      className={`flex items-center gap-2 px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors ${
        view === "company" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
      }`}
    >
      <Building2 className="h-3.5 w-3.5" strokeWidth={1.75} />
      Company
    </button>
    {hasMy && (
      <button
        onClick={() => setView("me")}
        data-testid="view-me"
        className={`flex items-center gap-2 px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors ${
          view === "me" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
        }`}
      >
        <User className="h-3.5 w-3.5" strokeWidth={1.75} />
        My share
      </button>
    )}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [mtm, setMtm] = useState(null);
  const [mine, setMine] = useState(null);
  const [view, setView] = useState("company");
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ preset: "month", ...computeRange("month") });
  const [fy, setFy] = useState("all");

  const hasPartnerId = !!user?.partner_id;
  const hasOwnView = hasPartnerId || user?.role === "investor";

  // Filter the 12-month series by picked range
  const filteredIncome12m = useMemo(() => {
    if (!company?.income_12m) return [];
    if (!range.from_date && !range.to_date) return company.income_12m;
    return company.income_12m.filter((m) => {
      const key = `${m.key}-01`;
      if (range.from_date && key < range.from_date) return false;
      if (range.to_date && key > range.to_date) return false;
      return true;
    });
  }, [company, range]);
  const rangeTotalIncome = useMemo(
    () => filteredIncome12m.reduce((a, m) => a + (m.amount || 0), 0),
    [filteredIncome12m]
  );
  const rangeLabel = range.preset === "month" ? "This month" :
                     range.preset === "week"    ? "This week" :
                     range.preset === "today"   ? "Today" :
                     range.preset === "quarter" ? "This quarter" :
                     range.preset === "fy"      ? "This FY" :
                     range.preset === "all"     ? "All-time income" : "Range";

  useEffect(() => {
    const jobs = [api.get("/dashboard/summary")];
    if (hasOwnView) jobs.push(api.get("/me/summary"));
    Promise.all(jobs).then((r) => {
      setCompany(r[0].data);
      if (hasOwnView) setMine(r[1].data);
      // Default partners and investors to their own view
      if ((user?.role === "partner" || user?.role === "investor") && hasOwnView) setView("me");
      setLoading(false);
    });
    api.get("/policies/alerts").then((r) => setComplianceAlerts(r.data)).catch(() => {});
    api.get("/portfolio/mtm").then((r) => setMtm(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [hasPartnerId]);

  if (loading || !company) return <div className="text-slate-500 text-sm">Loading…</div>;

  if (loading || !company) return <div className="text-slate-500 text-sm">Loading…</div>;

  const isMyView = view === "me" && mine;

  const t = isMyView ? {
    total_invested: mine.totals.my_invested,
    current_value: mine.totals.my_current_value,
    total_profit: mine.totals.my_profit,
    return_percent: mine.totals.my_return_percent,
    income_this_month: mine.totals.my_this_month,
  } : company.kpi;

  const investments = isMyView
    ? mine.investments.map((i) => ({
        ...i,
        total_invested: i.my_invested,
        current_value: i.my_current_value,
        profit: i.my_profit,
        return_percent: i.total_invested > 0 ? (i.profit / i.total_invested * 100) : 0,
      }))
    : company.investments;

  const allocation = investments.map((c) => ({ name: c.name, value: c.current_value, type: c.type }))
    .filter((a) => a.value > 0);

  return (
    <div className="space-y-8 md:space-y-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">
            {isMyView
              ? (mine.partner ? `Your share · ${mine.partner.ownership_percent}%` : "Your investments")
              : "Overview · Today"}
          </div>
          <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="dashboard-title">
            {isMyView ? <>My <em className="italic">portfolio.</em></> : <>The <em className="italic">ledger,</em> at a glance.</>}
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-md">
            {isMyView
              ? `${mine.partner?.name || user?.name || "Investor"}, this is what you own and what it's earning.`
              : "Everything MARKSAM owns, earns and owes — kept plain."}
          </p>
        </div>
        {user?.role === "partner" && hasPartnerId && <ViewToggle view={view} setView={setView} hasMy />}
      </div>

      {/* Quick date filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="eyebrow">Range</span>
        <DateRangeFilter value={range} onChange={setRange} testid="dashboard-range" />
        <FYPicker
          value={fy}
          onChange={(v) => {
            setFy(v);
            const { from, to } = fyToRange(v);
            setRange({ preset: v === "all" ? "all" : "fy", from_date: from, to_date: to });
          }}
          size="sm"
        />
        <span className="text-xs text-slate-500 ml-auto font-mono-fin">
          {range.from_date || "—"} → {range.to_date || "today"}
        </span>
      </div>

      {/* KPI grid — 2 cols mobile, 5 on desktop */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <KPI testid="kpi-invested" label={isMyView ? "You put in" : "Total Invested"} value={formatRupeesVisual(t.total_invested)} sub={formatRupees(t.total_invested)} />
        <KPI testid="kpi-value" label={isMyView ? "Your share value" : "Current Value"} value={formatRupeesVisual(t.current_value)} sub={formatRupees(t.current_value)} />
        <KPI testid="kpi-profit"
          label={isMyView ? "Your profit" : "Cash Profit"}
          value={formatRupeesVisual(isMyView ? t.total_profit : (t.realised_profit ?? t.total_profit))}
          sub={isMyView
            ? formatRupees(t.total_profit)
            : `${formatRupees(t.realised_profit ?? 0)} · receipts − payments`}
          positive={(isMyView ? t.total_profit : (t.realised_profit ?? 0)) >= 0}
          negative={(isMyView ? t.total_profit : (t.realised_profit ?? 0)) < 0} />
        <KPI testid="kpi-return" label="Return" value={formatPercent(t.return_percent)} sub="Since start"
          positive={t.return_percent >= 0} negative={t.return_percent < 0} />
        <KPI testid="kpi-month-income" label={rangeLabel} value={formatRupeesVisual(range.preset === "month" ? t.income_this_month : rangeTotalIncome)} sub={formatRupees(range.preset === "month" ? t.income_this_month : rangeTotalIncome)} />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-4 lg:gap-6">
        <div className="lg:col-span-1 border border-slate-200 p-5 lg:p-6 bg-white rounded-md" data-testid="allocation-chart">
          <div className="mb-4">
            <div className="eyebrow">Allocation</div>
            <h3 className="font-editorial font-light text-xl md:text-2xl text-slate-900 mt-1 tracking-tight">Where money <em className="italic">is.</em></h3>
          </div>
          <div className="h-56">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={allocation} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                  {allocation.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="white" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatRupees(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-1.5">
            {allocation.map((a, i) => (
              <div key={a.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-2.5 w-2.5 shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="text-slate-700 truncate">{a.name}</span>
                </div>
                <span className="font-mono-fin text-slate-900 shrink-0 ml-2">{formatRupeesVisual(a.value)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 border border-slate-200 p-5 lg:p-6 bg-white rounded-md" data-testid="income-chart">
          <div className="mb-4">
            <div className="eyebrow">Last 12 months</div>
            <h3 className="font-editorial font-light text-xl md:text-2xl text-slate-900 mt-1 tracking-tight">Monthly <em className="italic">income.</em></h3>
          </div>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={filteredIncome12m}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#CBD5E1" }} tickLine={false} />
                <YAxis tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={50} />
                <Tooltip formatter={(v) => formatRupees(v)} cursor={{ fill: "#FEF2F2" }} />
                <Bar dataKey="amount" fill="#DC2626" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Mark-to-market portfolio card */}
      {mtm && mtm.summary && (
        <Link to="/market-valuation" className="block border border-slate-200 bg-white rounded-md p-4 sm:p-5 hover:border-slate-300 hover:shadow-sm transition-all" data-testid="mtm-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-slate-500" />
            <div className="eyebrow">Portfolio · Mark-to-market</div>
            <div className="ml-auto text-[11px] text-slate-500">
              as of {mtm.as_of_date} · <span className="text-red-600 underline">open report →</span>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <div className="text-[10px] tracking-wider uppercase text-slate-500">Cost</div>
              <div className="mt-1 font-mono-fin text-xl font-semibold text-slate-900" data-testid="dash-mtm-cost">{formatRupeesVisual(mtm.summary.total_cost)}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-wider uppercase text-slate-500">Market value</div>
              <div className="mt-1 font-mono-fin text-xl font-semibold text-slate-900" data-testid="dash-mtm-value">{formatRupeesVisual(mtm.summary.total_market_value)}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-wider uppercase text-slate-500">Unrealised gain</div>
              <div className={`mt-1 font-mono-fin text-xl font-semibold ${mtm.summary.unrealised_gain >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="dash-mtm-gain">
                {mtm.summary.unrealised_gain >= 0 ? "+" : ""}{formatRupeesVisual(mtm.summary.unrealised_gain)}
                <span className="ml-2 text-[11px] font-normal">({mtm.summary.gain_percent >= 0 ? "+" : ""}{mtm.summary.gain_percent.toFixed(2)}%)</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-wider uppercase text-slate-500">XIRR</div>
              <div className="mt-1 font-mono-fin text-xl font-semibold text-slate-900" data-testid="dash-mtm-xirr">
                {mtm.summary.xirr_percent === null || mtm.summary.xirr_percent === undefined ? "—" : `${mtm.summary.xirr_percent.toFixed(2)}%`}
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* Alerts */}
      {(company.alerts.length > 0 || complianceAlerts.length > 0) && (
        <div className="border border-red-200 bg-red-50 p-4 sm:p-5 rounded-md" data-testid="alerts-box">
          <div className="flex items-center gap-2 text-red-700 font-semibold text-sm">
            <AlertTriangle className="h-4 w-4" />
            Needs attention ({company.alerts.length + complianceAlerts.length})
          </div>
          <ul className="mt-3 space-y-2">
            {company.alerts.map((a, i) => (
              <li key={`i-${i}`} className="text-sm text-red-900 flex gap-2" data-testid={`alert-${i}`}>
                <span className={`inline-block h-1.5 w-1.5 mt-2 shrink-0 ${a.level === "danger" ? "bg-red-600" : "bg-yellow-500"}`} />
                <span>{a.message}</span>
              </li>
            ))}
            {complianceAlerts.map((a, i) => (
              <li key={`c-${i}`} className="text-sm text-red-900 flex gap-2" data-testid={`compliance-alert-${i}`}>
                <span className={`inline-block h-1.5 w-1.5 mt-2 shrink-0 ${a.days < 0 ? "bg-red-600" : "bg-yellow-500"}`} />
                <span>
                  <Link to="/compliance" className="underline hover:text-red-700">
                    {a.title}
                  </Link>
                  {" "}
                  <span className="text-red-700/70">
                    ({a.type_name}) — {
                      a.reason === "expired" ? `expired ${Math.abs(a.days)}d ago` :
                      a.reason === "expiring_soon" ? `expires in ${a.days}d` :
                      a.reason === "premium_overdue" ? `premium overdue ${Math.abs(a.days)}d` :
                      `premium due in ${a.days}d`
                    }
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Investments — mobile cards, desktop table */}
      <div>
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="eyebrow">All investments</div>
            <h3 className="font-editorial font-light text-xl md:text-2xl text-slate-900 mt-1 tracking-tight">The <em className="italic">portfolio.</em></h3>
          </div>
          <Link to="/investments" data-testid="view-all-investments" className="text-sm font-medium text-slate-700 hover:text-red-600 inline-flex items-center gap-1">
            All <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {/* Mobile card list */}
        <div className="grid gap-3 md:hidden">
          {investments.map((inv) => (
            <Link
              to={`/investments/${inv.id}`}
              key={inv.id}
              data-testid={`inv-link-${inv.id}`}
              className="border border-slate-200 bg-white p-4 rounded-md hover-lift"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{INVESTMENT_TYPE_LABEL[inv.type]}</div>
                  <div className="mt-1 font-medium text-slate-900 truncate">{inv.name}</div>
                </div>
                <div className={`font-mono-fin font-semibold text-sm ${inv.return_percent >= 0 ? "text-green-700" : "text-red-600"}`}>
                  {formatPercent(inv.return_percent)}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 pt-3 border-t border-slate-100 text-[11px]">
                <div>
                  <div className="text-slate-400">Invested</div>
                  <div className="font-mono-fin text-slate-900 mt-0.5">{formatRupeesCompact(inv.total_invested)}</div>
                </div>
                <div>
                  <div className="text-slate-400">Value</div>
                  <div className="font-mono-fin text-slate-900 mt-0.5">{formatRupeesCompact(inv.current_value)}</div>
                </div>
                <div>
                  <div className="text-slate-400">Profit</div>
                  <div className={`font-mono-fin mt-0.5 ${inv.profit >= 0 ? "text-green-700" : "text-red-600"}`}>{formatRupeesCompact(inv.profit)}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block border border-slate-200 rounded-md overflow-x-auto thin-scroll bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left">
                <th className="p-3 text-[10px] tracking-wider uppercase text-slate-500">Name</th>
                <th className="p-3 text-[10px] tracking-wider uppercase text-slate-500">Type</th>
                <th className="p-3 text-[10px] tracking-wider uppercase text-slate-500 text-right">Invested</th>
                <th className="p-3 text-[10px] tracking-wider uppercase text-slate-500 text-right">Current</th>
                <th className="p-3 text-[10px] tracking-wider uppercase text-slate-500 text-right">Profit</th>
                <th className="p-3 text-[10px] tracking-wider uppercase text-slate-500 text-right">Return</th>
              </tr>
            </thead>
            <tbody>
              {investments.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50/70">
                  <td className="p-3">
                    <Link to={`/investments/${inv.id}`} className="text-slate-900 font-medium hover:text-red-600">
                      {inv.name}
                    </Link>
                  </td>
                  <td className="p-3 text-slate-600">{INVESTMENT_TYPE_LABEL[inv.type]}</td>
                  <td className="p-3 text-right font-mono-fin text-slate-900">{formatRupeesCompact(inv.total_invested)}</td>
                  <td className="p-3 text-right font-mono-fin text-slate-900">{formatRupeesCompact(inv.current_value)}</td>
                  <td className={`p-3 text-right font-mono-fin ${inv.profit >= 0 ? "text-green-700" : "text-red-600"}`}>
                    {formatRupeesCompact(inv.profit)}
                  </td>
                  <td className={`p-3 text-right font-mono-fin ${inv.return_percent >= 0 ? "text-green-700" : "text-red-600"}`}>
                    {formatPercent(inv.return_percent)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
